import {colors} from './colors';
import useWindowDimensions from '../utils/hooks/useWindowDimensions';
import {getByScreenSize, hdp, wdp} from '../utils/responsive';
import {Theme} from './lightTheme';

/**
 * Defines the dark theme for the application.
 *
 * This function returns a comprehensive object containing styles and colors for the dark theme.
 *
 * @returns - An object representing the dark theme, including color palettes, fonts, text styles, and component-specific styles.
 */
const height = window.innerHeight;

const darkTheme = (): Theme => {
  const {isMobile} = useWindowDimensions();
  return {
    primary: colors.primary,
    secondary: colors.secondary,
    ripple: '#3D3D3D',
    overlay: 'rgba(0,0,0,0.5)',
    headerBackground: '#181818',
    homeBackground: '#181818',
    cardBackground: 'rgba(30,30,30,0.95)',
    cardBackgroundEnd: 'rgba(25,25,25,0.95)',
    icon: colors.secondary,
    skeletonBackground: '#2D2D2D',
    border: '#3D3D3D',
    primaryOverlay: 'rgba(40, 35, 245, 0.2)',
    backgroundOverlay: 'rgba(0,0,0,0.2)',
    imageBackground: '#1E1E1E',
    background: '#181818',
    overlayScreen: 'rgba(0,0,0,0.7)',
    defaultOverlayScreen: 'rgba(0,0,0,0.3)',
    shadow: 'rgba(0,0,0,0.6)',
    dimensions: {
      hdp: (percent: number) => hdp(percent),
      wdp: (percent: number) => wdp(percent),
      getByScreenSize: (smValue: any, lgValue: any, xsmValue: any = smValue) =>
        getByScreenSize(smValue, lgValue, xsmValue),
    },
    isMobile,
    alert: {
      backgroundPrimary: '#3D3D3D',
      textPrimary: '#FFFFFF',
    },
    fonts: {
      ar: '"BBC Reith Sans", Arial, sans-serif',
      en: '"BBC Reith Sans", Arial, sans-serif',
    },
    text: {
      dark: '#F5F5F5',
      black: '#FFFFFF',
      border: '#3D3D3D',
      icon: '#A1A1A1',
      error: '#ff5252',
      success: '#4caf50',
      grey: '#9E9E9E',
      greyLight: '#707070',
      title: '#FFFFFF',
      description: '#9E9E9E',
      light: '#FFFFFF',
      optionIcon: '#cccccc',
      s1: height >= 900 ? '4.5rem' : '4rem',
      s2: height >= 900 ? '4rem' : '3.25rem',
      s3: height >= 900 ? '2.75rem' : '2rem',
      s35: height >= 900 ? '1.8rem' : '1.4rem',
      s4: height >= 900 ? '1.5rem' : '1.2rem',
      s5: height >= 900 ? '1.4rem' : '1rem',
      s55: height >= 900 ? '1.2rem' : '0.8rem',
      s6: height >= 900 ? '1.2rem' : '0.8rem',
      s7: height >= 900 ? '1rem' : '0.75rem',
      s8: height >= 900 ? '0.875rem' : '0.7rem',
      s85: height >= 900 ? '0.875rem' : '0.7rem',
      s89: height >= 900 ? '0.825rem' : '0.675rem',
      s9: height >= 900 ? '0.8rem' : '0.65rem',
      s10: height >= 900 ? '0.7rem' : '0.6rem',
      s11: height >= 900 ? '0.65rem' : '0.55rem',
      s12: height >= 900 ? '0.5rem' : '0.4rem',
    },
    button: {
      text: '#FFFFFF',
      lightColor: '#FFFFFF',
      outline: colors.primary,
    },
    textInput: {
      cursorColor: colors.primary,
      placeholder: '#9E9E9E',
      border: '#3D3D3D',
      required: '#ffd2d2',
      background: '#242424',
    },
    fab: {
      backgroundColor: '#242424',
    },
    header: {
      primary: colors.secondary,
      startLinearGradient: 'rgba(30,30,30,0.8)',
      endLinearGradient: 'rgba(25,25,25,0.8)',
    },
    bottomTabs: {
      barBackground: 'rgba(30,30,30,0.9)',
      activeTabBackground: colors.secondary + 'EE',
      label: '#FFFFFF',
      icon: colors.secondary + 'EE',
      iconFocus: '#FFFFFF',
    },
    activityListItem: {
      startColor: '#242424',
      endColor: '#1E1E1E',
    },
    drawer: {
      bgColor1: '#006562AA',
      bgColor2: '#004a6cAA',
      fontColor: '#ffffff',
      fontColorSelected: '#D9D9D9',
      dividerColor: '#CCCCCC',
      selectedBackgroundCollapsedMode: 'dark',
    },
    switch: {
      trackOff: '#3D3D3D',
      trackOn: colors.secondary,
      thumb: '#F5F5F5',
    },
    search: {
      placeholder: '#F5F5F5',
    },
    questionInput: {
      text: '#FFFFFF',
      placeholder: '#707070',
      startColor: 'rgba(30,30,30,0.7)',
      endColor: 'rgba(25,25,25,0.4)',
    },
    voiceQuestionInput: {
      text: '#F5F5F5',
      background: '#242424',
      inputPlaceholder: 'rgba(255,255,255,0.1)',
      iosEndColor: 'rgba(0,0,0,0)',
      mainButtonStartColor: colors.primary,
      mainButtonEndColor: colors.secondary,
    },
    afterLogin: {
      contentBackground: '#242424',
      text: '#F5F5F5',
      label: '#9E9E9E',
      line: '#3D3D3D',
      selected: 'rgba(255,255,255,0.1)',
      checkIcon: '#3D3D3D',
      checkIconBackground: colors.primary,
    },
    chatBubble: {
      backgroundSend: colors.secondary,
      backgroundReceived: '#242424',
      selected: 'rgba(255,255,255,0.1)',
      date: colors.secondary,
      error: '#ffdfdf',
      favorite: '#FFD707',
      sendText: '#FFFFFF',
      receivedText: '#F5F5F5',
      online: '#4caf50',
      offline: '#9E9E9E',
    },
    onboarding: {
      text: '#FFFFFF',
      background: '#0B0D6F',
      textSecondary: colors.tertiary,
    },
    home: {
      cardBackground: '#242424',
    },
    link: {
      background: '#515151',
      color: '#D3D3D3',
    },
    chatBottomBar: {
      sendIcon: '#000000',
      background: '#ffffff',
    },
    chatList: {
      text: '#D3D3D3',
    },
    table: {
      thead: '#2b2b2b',
      text: '#e0e0e0',
      tr: '#2a2a2a',
      trHover: '#444444',
      evenRow: '#2a2a2a',
    },
    scrollbar: {
      track: 'transparent',
      thumb: 'rgba(0,123,178,0.5)',
      thumbHover: 'rgba(0,95,135,0.5)',
    },
    dropdown: {
      background: '#2e2e2e',
      color: '#e3e3e3',
      delete: '#f93a37',
    },
    sidebar: {
      background: '#000',
      shadow: '#ececf1',
      hover: '#343541',
    },
  };
};

export default darkTheme;
